import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

/**
 * Built-in variable speed algorithm that maintains smooth animation
 * until the buffer grows large, then gradually accelerates.
 *
 * < 300 chars: User's preferred speed (smooth reading experience)
 * 300-750 chars: Gradual acceleration from 1.5x to 2x
 * > 750 chars: Faster acceleration up to 5x max
 *
 * This creates a natural reading experience that only speeds up
 * when there's a significant content backlog.
 */
const defaultVariableSpeed = (
  bufferSize: number,
  baseSpeed: number
): number => {
  const COMFORT_THRESHOLD = 300;
  const URGENCY_THRESHOLD = 750;

  if (bufferSize < COMFORT_THRESHOLD) {
    return baseSpeed;
  }

  if (bufferSize < URGENCY_THRESHOLD) {
    const progress =
      (bufferSize - COMFORT_THRESHOLD) /
      (URGENCY_THRESHOLD - COMFORT_THRESHOLD);
    const multiplier = 1.5 + 0.5 * progress;
    return Math.floor(baseSpeed * multiplier);
  }

  const excessBuffer = bufferSize - URGENCY_THRESHOLD;
  const additionalMultiplier = Math.min(excessBuffer / 500, 3);
  return Math.floor(baseSpeed * (2 + additionalMultiplier));
};

export interface BatchAwareSpeedBurstStats {
  bufferSize: number;
  observedGapMs: number | null;
  emaGapMs: number;
  adaptiveWindowMs: number;
  reserveCharacters: number;
}

interface BatchAwareSpeedOptions {
  /** Expected backend flush interval in seconds */
  batchWindowSec?: number;
  /** Target frame rate used for pacing calculations */
  fps?: number;
  /** Lower bound to keep animation progressing while buffer > 0 */
  minCharactersPerFrame?: number;
  /** Optional upper bound to avoid very large per-frame jumps */
  maxCharactersPerFrame?: number;
  /** Tiny buffer-growth threshold used to detect new incoming bursts */
  burstEpsilon?: number;
  /** If no frames run for this long, reset burst tracking */
  resetGapMs?: number;
  /** Lower clamp for adaptive pacing window */
  minWindowMs?: number;
  /** Upper clamp for adaptive pacing window */
  maxWindowMs?: number;
  /** EMA alpha for observed burst-gap updates (0..1) */
  emaAlpha?: number;
  /** Extra headroom added on top of estimated burst gap */
  jitterMarginMs?: number;
  /** Keep this many chars in reserve to smooth jitter */
  reserveCharacters?: number;
  /** Wait this long past expected burst time before reserve starts decaying */
  lateGraceMs?: number;
  /** Optional callback fired whenever a new burst is detected */
  onBurst?: (stats: BatchAwareSpeedBurstStats) => void;
  /** When true, bypass batch-aware pacing and drain with the default variable-speed style */
  fastFinish?: boolean;
  /** Soft upper bound window (seconds) for fast-finish drain */
  fastFinishWindowSec?: number;
}

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const intlWithSegmenter = Intl as typeof Intl & {
  Segmenter?: new (
    locales?: string | string[],
    options?: { granularity?: 'grapheme' | 'word' | 'sentence' }
  ) => { segment(input: string): Iterable<{ segment: string }> };
};

const graphemeSegmenter =
  typeof intlWithSegmenter.Segmenter === 'function'
    ? new intlWithSegmenter.Segmenter(undefined, { granularity: 'grapheme' })
    : null;

const getGraphemeEndIndices = (text: string): number[] => {
  if (!text) return [];

  const indices: number[] = [];
  let cursor = 0;

  if (graphemeSegmenter !== null) {
    for (const { segment } of graphemeSegmenter.segment(text)) {
      cursor += segment.length;
      indices.push(cursor);
    }
    return indices;
  }

  for (const char of text) {
    cursor += char.length;
    indices.push(cursor);
  }

  return indices;
};

/**
 * Creates a drain-rate function tuned for batched token delivery.
 *
 * Before we observe a real burst gap, pace against the initial expected window.
 * After the first observed gap, switch to fully adaptive batch-aware pacing.
 */
export function createBatchAwareSpeed({
  batchWindowSec = 1.3,
  fps = 60,
  minCharactersPerFrame = 0.08,
  maxCharactersPerFrame = 18,
  burstEpsilon = 0.001,
  resetGapMs = 500,
  minWindowMs = 1100,
  maxWindowMs = 2200,
  emaAlpha = 0.2,
  jitterMarginMs = 180,
  reserveCharacters = 120,
  lateGraceMs = 150,
  onBurst,
  fastFinish = false,
  fastFinishWindowSec = 0.24,
}: BatchAwareSpeedOptions = {}) {
  const safeFps = Math.max(1, fps);
  const frameMs = 1000 / safeFps;
  const initialWindowMs = Math.max(frameMs, batchWindowSec * 1000);
  const minimumWindowMs = Math.max(frameMs, Math.min(minWindowMs, maxWindowMs));
  const maximumWindowMs = Math.max(minimumWindowMs, maxWindowMs);
  const fastFinishWindowMs = Math.max(frameMs, fastFinishWindowSec * 1000);
  const alpha = clamp(emaAlpha, 0, 1);
  const minimumRate = Math.max(0, minCharactersPerFrame);
  const maximumRate = Math.max(minimumRate, maxCharactersPerFrame);

  let previousBufferSize = 0;
  let lastTickAt = 0;
  let lastBurstAt = 0;
  let hasObservedBurstGap = false;
  let emaGapMs = clamp(initialWindowMs, minimumWindowMs, maximumWindowMs);
  let adaptiveWindowMs = clamp(
    emaGapMs + jitterMarginMs,
    minimumWindowMs,
    maximumWindowMs
  );

  return (bufferSize: number, baseSpeed: number): number => {
    const now = performance.now();

    const hasIdleGap = lastTickAt > 0 && now - lastTickAt > resetGapMs;
    if (hasIdleGap) {
      previousBufferSize = 0;
      lastBurstAt = 0;
      hasObservedBurstGap = false;
      emaGapMs = clamp(initialWindowMs, minimumWindowMs, maximumWindowMs);
      adaptiveWindowMs = clamp(
        emaGapMs + jitterMarginMs,
        minimumWindowMs,
        maximumWindowMs
      );
    }

    if (!Number.isFinite(bufferSize) || bufferSize <= 0) {
      previousBufferSize = 0;
      lastTickAt = 0;
      lastBurstAt = 0;
      hasObservedBurstGap = false;
      emaGapMs = clamp(initialWindowMs, minimumWindowMs, maximumWindowMs);
      adaptiveWindowMs = clamp(
        emaGapMs + jitterMarginMs,
        minimumWindowMs,
        maximumWindowMs
      );
      return 0;
    }

    const hasIncomingBurst = bufferSize > previousBufferSize + burstEpsilon;
    if (hasIncomingBurst) {
      let observedGapMs: number | null = null;
      if (lastBurstAt > 0) {
        const rawGapMs = now - lastBurstAt;
        if (Number.isFinite(rawGapMs) && rawGapMs > 0) {
          observedGapMs = rawGapMs;
          hasObservedBurstGap = true;
          emaGapMs = clamp(
            alpha * observedGapMs + (1 - alpha) * emaGapMs,
            minimumWindowMs,
            maximumWindowMs
          );
        }
      }
      lastBurstAt = now;
      adaptiveWindowMs = clamp(
        emaGapMs + jitterMarginMs,
        minimumWindowMs,
        maximumWindowMs
      );

      const activeReserveCharacters = hasObservedBurstGap
        ? reserveCharacters
        : 0;

      onBurst?.({
        bufferSize,
        observedGapMs,
        emaGapMs,
        adaptiveWindowMs,
        reserveCharacters: activeReserveCharacters,
      });
    }

    if (fastFinish) {
      const finishFrames = Math.max(
        1,
        Math.round(fastFinishWindowMs / frameMs)
      );
      const finishWindowRate = bufferSize / finishFrames;
      const nonBatchRate = defaultVariableSpeed(bufferSize, baseSpeed);
      const targetRate = Math.min(nonBatchRate, finishWindowRate);

      previousBufferSize = bufferSize;
      lastTickAt = now;

      return Math.min(maximumRate, Math.max(minimumRate, targetRate));
    }

    if (!hasObservedBurstGap) {
      const elapsedSinceBurstMs =
        lastBurstAt > 0 ? Math.max(0, now - lastBurstAt) : 0;
      const untilExpectedBurstMs = Math.max(
        frameMs,
        adaptiveWindowMs - elapsedSinceBurstMs
      );
      const framesUntilExpectedBurst = Math.max(
        1,
        Math.round(untilExpectedBurstMs / frameMs)
      );
      const targetRate = bufferSize / framesUntilExpectedBurst;

      previousBufferSize = bufferSize;
      lastTickAt = now;
      return Math.min(maximumRate, Math.max(minimumRate, targetRate));
    }

    const elapsedSinceBurstMs =
      lastBurstAt > 0 ? Math.max(0, now - lastBurstAt) : 0;

    const untilExpectedBurstMs = Math.max(
      frameMs,
      adaptiveWindowMs - elapsedSinceBurstMs
    );
    const framesUntilExpectedBurst = Math.max(
      1,
      Math.round(untilExpectedBurstMs / frameMs)
    );

    const lateMs = Math.max(
      0,
      elapsedSinceBurstMs - adaptiveWindowMs - lateGraceMs
    );
    const lateDecayProgress = Math.min(
      1,
      lateMs / Math.max(frameMs, adaptiveWindowMs)
    );
    const effectiveReserveChars = Math.max(
      0,
      reserveCharacters * (1 - lateDecayProgress)
    );
    const bufferToDrain = Math.max(0, bufferSize - effectiveReserveChars);

    const targetRate = bufferToDrain / framesUntilExpectedBurst;

    previousBufferSize = bufferSize;
    lastTickAt = now;

    return Math.min(maximumRate, Math.max(minimumRate, targetRate));
  };
}

export interface SmoothingOptions {
  /** Start / stop the streaming animation (default: true = on) */
  enabled?: boolean;
  /** How many characters to reveal **per animation frame** (≥ 1) */
  charactersPerFrame?: number;
  /** Dynamic characters per frame based on buffer size. Can return fractional values */
  variableCharactersPerFrame?: (
    bufferSize: number,
    baseSpeed: number
  ) => number;
  /** When `enabled` flips **off**: show the whole text instantly? */
  flushOnDisable?: boolean; // default: false (pause only)
  /** Debounce delay for that flush (only if ^ is true) */
  flushDebounceMs?: number; // default: 250 ms
  /** Callback fired the first time the loop starts (or restarts) */
  onAnimationStart?: () => void;
  /** flushed=true on any non-natural completion (flushed, paused, or
   *  unmounted mid-stream). Gate user-visible side effects on `!flushed`. */
  onAnimationComplete?: (flushed: boolean) => void;
  /** Show initial content immediately on mount instead of animating from start (default: false)
   */
  adoptInitialContent?: boolean;
}

/**
 * Streams the `content` string, revealing `charactersPerFrame`
 *    characters every `requestAnimationFrame`.
 *  • Pauses automatically when the document is hidden
 *    (background tab / minimised window).
 *  • On visibility-return, flushes everything we missed, then
 *    resumes smooth animation for new chunks.
 *  • Optional "flush immediately when disabled" behaviour.
 */
export function useSmoothedContent(
  content: string,
  {
    enabled = true,
    charactersPerFrame = 3,
    variableCharactersPerFrame = defaultVariableSpeed,
    flushOnDisable = false,
    flushDebounceMs = 250,
    adoptInitialContent = false,
    onAnimationStart,
    onAnimationComplete,
  }: SmoothingOptions = {}
) {
  const graphemeEndIndices = useMemo(
    () => getGraphemeEndIndices(content),
    [content]
  );
  const graphemeCount = graphemeEndIndices.length;

  /** Visible text */
  const [smoothed, setSmoothed] = useState(() =>
    adoptInitialContent ? content : ''
  );
  /** Next grapheme index to reveal */
  const idxRef = useRef(adoptInitialContent ? graphemeCount : 0);
  /** requestAnimationFrame` id */
  const rafRef = useRef<number | null>(null);
  /** Debounce timer id for the flush */
  const flushIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Are we mid-animation? **/
  const streamingRef = useRef(false);
  /** Variable speed configuration */
  const variableCharactersPerFrameRef = useRef(variableCharactersPerFrame);
  /** Fractional carry for speeds below 1 char/frame */
  const fractionalCarryRef = useRef(0);
  /** Latest onAnimationComplete prop, for the unmount-only effect below */
  const onAnimationCompleteRef = useRef(onAnimationComplete);
  /** Previous content seen by the content-change effect */
  const prevContentRef = useRef(content);
  /** Check if the document is visible */
  const isVisible = () => document.visibilityState === 'visible';

  // Update refs when props change
  variableCharactersPerFrameRef.current = variableCharactersPerFrame;
  onAnimationCompleteRef.current = onAnimationComplete;

  /** Helper: cancel any pending  animation */
  const cancelRaf = () => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const finishStreaming = () => {
    streamingRef.current = false;
    fractionalCarryRef.current = 0;
    onAnimationComplete?.(false); // natural completion
    cancelRaf();
  };

  /**
   * Core animation loop
   */

  const animate = () => {
    /* Stop if disabled or tab hidden */
    if (!enabled || !isVisible()) return;

    const bufferSize = graphemeCount - idxRef.current;
    const variableSpeed = variableCharactersPerFrameRef.current;

    const rawStep = variableSpeed
      ? variableSpeed(bufferSize, charactersPerFrame)
      : charactersPerFrame;

    const normalizedStep = Number.isFinite(rawStep) ? Math.max(0, rawStep) : 0;
    const stepWithCarry = normalizedStep + fractionalCarryRef.current;
    const step = Math.floor(stepWithCarry);
    fractionalCarryRef.current = stepWithCarry - step;

    /* Reveal up to `step` more chars */
    const next = Math.min(idxRef.current + step, graphemeCount);
    if (next > idxRef.current) {
      const nextCodeUnitIndex = graphemeEndIndices[next - 1] ?? 0;
      setSmoothed(content.slice(0, nextCodeUnitIndex));
      idxRef.current = next;
    }

    /* End-of-string? */
    if (idxRef.current >= graphemeCount) {
      finishStreaming();
    } else {
      rafRef.current = requestAnimationFrame(animate);
    }
  };

  /* Start (or restart) the loop */
  const startLoop = () => {
    cancelRaf();
    if (!enabled || !isVisible()) return; // don't animate in background
    if (!streamingRef.current) onAnimationStart?.();
    streamingRef.current = true;
    rafRef.current = requestAnimationFrame(animate);
  };

  /**
   * 1. Visibility handling
   *    • Pause immediately when hidden
   *    • Flush backlog instantly when visible again
   */
  useLayoutEffect(() => {
    const handleVisibility = () => {
      if (isVisible()) {
        /* Flush all missed characters */
        setSmoothed(content);
        idxRef.current = graphemeCount;
        fractionalCarryRef.current = 0;
        startLoop(); // keep animating new chunks
      } else {
        cancelRaf(); // stop loop while hidden
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibility);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, enabled, charactersPerFrame, variableCharactersPerFrame]);

  /**
   * 2. Pause / flush when `enabled` goes false
   */
  useLayoutEffect(() => {
    if (enabled) return;

    cancelRaf();

    /* Only pause? → done */
    if (!flushOnDisable) {
      streamingRef.current = false;
      fractionalCarryRef.current = 0;
      onAnimationComplete?.(true); // flushed=true (paused)
      return;
    }

    /* Otherwise flush after debounce */
    if (flushIdRef.current) clearTimeout(flushIdRef.current);
    flushIdRef.current = setTimeout(() => {
      setSmoothed(content);
      idxRef.current = graphemeCount;
      fractionalCarryRef.current = 0;
      streamingRef.current = false;
      onAnimationComplete?.(true); // flushed=true (flushed)
      flushIdRef.current = null;
    }, flushDebounceMs);
  }, [
    enabled,
    content,
    flushOnDisable,
    flushDebounceMs,
    onAnimationComplete,
    graphemeCount,
  ]);

  /**
   * 3. Respond to `content`, speed, or enable changes
   */
  useLayoutEffect(() => {
    const prevContent = prevContentRef.current;
    prevContentRef.current = content;

    /* External edit detection: we'd already revealed all of the prior
     * content (`smoothed === prevContent`) and the new content isn't an
     * append (it doesn't start with the prior content). Snap to the new
     * value instead of resetting the animator and replaying from the
     * start - the latter looks like the whole message re-streams when a
     * caller (e.g. onCopyableEdit) rebuilds the message string. */
    if (
      content !== prevContent &&
      smoothed === prevContent &&
      !content.startsWith(prevContent)
    ) {
      cancelRaf();
      setSmoothed(content);
      idxRef.current = graphemeCount;
      fractionalCarryRef.current = 0;
      streamingRef.current = false;
      return cancelRaf;
    }

    if (graphemeCount < idxRef.current) {
      idxRef.current = graphemeCount;
      fractionalCarryRef.current = 0;
    }
    if (!content.startsWith(prevContent)) {
      setSmoothed(
        content.slice(0, graphemeEndIndices[idxRef.current - 1] ?? 0)
      );
    }

    /* No new characters? don't restart the loop */
    if (graphemeCount === idxRef.current) {
      if (streamingRef.current) finishStreaming();
      return cancelRaf;
    }

    startLoop(); // (re)launch the animator
    return cancelRaf; // clean up when deps change/unmount

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, enabled, charactersPerFrame, variableCharactersPerFrame]);

  /**
   * 4. Unmount-only completion: callers tracking animation state of their
   *    own need a signal when the host is torn down mid-stream.
   */
  useEffect(() => {
    return () => {
      if (flushIdRef.current) {
        clearTimeout(flushIdRef.current);
        flushIdRef.current = null;
      }
      if (!streamingRef.current) return;
      streamingRef.current = false;
      onAnimationCompleteRef.current?.(true);
    };
  }, []);

  if (!enabled && idxRef.current === 0) return content;

  return smoothed;
}
